import React, { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import styled from 'styled-components';
import { ReportQuestion } from '../utils';
import { Answer } from 'donor/portal/pages/FeedbackDashboard/Feedback/api';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { BottomBar } from '../../BottomBar';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import {
  UpdateReportResponse,
  updateIndicatorAnswer,
} from '../../ImpactIndicator/api';
import { defaultCostValue } from '../../Budget/RelevancyCheckbox';
import {
  FINAL_BUDGET_QUESTION_ID,
  getBudgetDollarAmount,
  getTotalBudget,
  INTERIM_BUDGET_QUESTION_ID,
} from '../../Budget/utils';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import TrueImpactCapsule from 'shared/design-system/components/TrueImpactCapsule';
import TrueImpactDollarField from 'shared/design-system/components/TrueImpactDollarField';
import { useClient } from 'shared/apollo';
import { Report } from 'shared/components/WelcomeBack/utils';
import TrueImpactButton from 'shared/design-system/components/TrueImpactButton';
import { numberWithCommas } from 'shared/utils/numberUtils';
import { getFinalizeMarkerQuestionId } from '../utils';

const getInitialAnswers = (answers?: Answer[]) => {
  let directCost = defaultCostValue;
  let overheadCost = defaultCostValue;
  let inKindDonations = defaultCostValue;
  if (!answers)
    return {
      directCost,
      overheadCost,
      inKindDonations,
    };
  for (const a of answers) {
    if (a.questionId === 80003) {
      const amount = getBudgetDollarAmount(a.text);
      directCost = amount || defaultCostValue;
    } else if (a.questionId === 80005) {
      const amount = getBudgetDollarAmount(a.text);
      inKindDonations = amount || defaultCostValue;
    } else if (a.questionId === 80007) {
      const amount = getBudgetDollarAmount(a.text);
      overheadCost = amount || defaultCostValue;
    }
  }
  return {
    directCost,
    overheadCost,
    inKindDonations,
  };
};

const getFinalAnswers = (
  answers?: Answer[],
  reportStage?: Report['stage'],
):
  | {
      directCost: string;
      overheadCost: string;
      inKindDonations: string;
      answerId: number | undefined;
    }
  | undefined => {
  if (!answers) return undefined;
  const questionId = reportStage
    ? getFinalizeMarkerQuestionId(5, reportStage)
    : FINAL_BUDGET_QUESTION_ID;
  if (!questionId) return undefined;
  const finalAnswers =
    answers.find(x => x.questionId === questionId) ||
    answers.find(x => x.questionId === INTERIM_BUDGET_QUESTION_ID);
  if (!finalAnswers) return undefined;
  try {
    return { ...JSON.parse(finalAnswers.text), answerId: finalAnswers.dbId };
  } catch (e) {
    return undefined;
  }
};

const validateFinalBudget = (
  initial: ReturnType<typeof getInitialAnswers>,
  final: Partial<ReturnType<typeof getFinalAnswers>>,
): boolean => {
  if (!final) return false;
  if (
    initial.directCost !== defaultCostValue &&
    (!final.directCost || final.directCost === defaultCostValue)
  )
    return false;
  if (
    initial.overheadCost !== defaultCostValue &&
    (!final.overheadCost || final.overheadCost === defaultCostValue)
  )
    return false;
  if (
    initial.inKindDonations !== defaultCostValue &&
    (!final.inKindDonations || final.inKindDonations === defaultCostValue)
  )
    return false;
  return true;
};

interface FinalizeBudgetProps {
  report: Report;
  answers: Answer[];
  isInterimStage: boolean;
  isFinalizeStage: boolean;
  questions: ReportQuestion[];
  reloadAnswers: () => unknown;
  onNextSection: () => unknown;
  onPreviousSection: () => unknown;
}

const StyledDiv = styled.div`
  margin-bottom: 100px;
  margin-top: ${SPACERS.xExtraLarge};
  .heading-page {
    padding-left: ${SPACERS.medium};
    padding-right: ${SPACERS.medium};
    margin-bottom: ${SPACERS.small};
  }
  .subheading-page {
    padding-left: ${SPACERS.medium};
    padding-right: ${SPACERS.medium};
    margin-bottom: ${SPACERS.extraLarge};
  }
  .table {
    max-width: 930px;
    margin-left: auto;
    margin-right: auto;
    padding-bottom: ${SPACERS.small};
    .body-forecast,
    .forecast-header {
      background-color: ${COLORS.grayLight};
    }
    .body-result,
    .results-header {
      background-color: ${COLORS.grayBase};
    }
    .capsule {
      border: 1px solid ${COLORS.grayLight};
      background-color: ${COLORS.whiteBase};
    }
    .header {
      display: grid;
      grid-template-columns: 1.3fr 1fr 1fr;
      border-left: 1px solid ${COLORS.grayLightest};
      border-radius: ${SPACERS.extraTiny} ${SPACERS.extraTiny} 0px 0px;
      background-color: ${COLORS.grayLightest};
      .stage-header,
      .forecast-header,
      .results-header {
        text-align: center;
      }
      .stage-header,
      .forecast-header,
      .results-header {
        padding: ${SPACERS.medium} 12px 12px 14px;
      }
    }
    .submitted-text {
      margin-top: ${SPACERS.tiny};
    }
    .table-body {
      margin-bottom: ${SPACERS.extraLarge};
      border-left: 1px solid ${COLORS.grayLight};
      border-bottom: 1px solid ${COLORS.grayLight};
      border-radius: 0 0 ${SPACERS.extraTiny} ${SPACERS.extraTiny};
      .dark-row {
        background-color: ${COLORS.grayDarkest} !important;
      }
      .table-body-row {
        display: grid;
        grid-template-columns: 1.3fr 1fr 1fr;
        &.table-body-row-border {
          border-bottom: 1px solid ${COLORS.grayLight};
        }
        .text-label {
          display: flex;
          align-items: center;
          padding: ${SPACERS.medium};
        }
        .text-cost {
          text-align: right;
          padding: ${SPACERS.large};
          background-color: ${COLORS.grayLight};
          &.text-cost-border {
            border-bottom: 1px solid ${COLORS.whiteBase};
          }
        }
        .dollar-field {
          padding: 20px ${SPACERS.medium};
          background-color: ${COLORS.grayBase};
          button {
            background-color: ${COLORS.whiteBase};
          }
        }
      }
    }
  }
`;

const FinalizeBudget: React.FC<FinalizeBudgetProps> = ({
  report,
  answers,
  isInterimStage,
  isFinalizeStage,
  reloadAnswers,
  onPreviousSection,
  onNextSection,
}) => {
  const client = useClient();
  const initialAnswers = useMemo(() => getInitialAnswers(answers), [answers]);
  const finalAnswers = useMemo(() => getFinalAnswers(answers, report.stage), [
    answers,
    report.stage,
  ]);
  const [directCostState, setDirectCostState] = useState<
    string | typeof defaultCostValue | undefined
  >(
    finalAnswers?.directCost ||
      (initialAnswers.directCost === defaultCostValue
        ? defaultCostValue
        : undefined),
  );
  const [overheadState, setOverheadState] = useState<string | undefined>(
    finalAnswers?.overheadCost ||
      (initialAnswers.overheadCost === defaultCostValue
        ? defaultCostValue
        : undefined),
  );
  const [inKindDonationState, setInKindDonationState] = useState<
    string | undefined
  >(
    finalAnswers?.inKindDonations ||
      (initialAnswers.inKindDonations === defaultCostValue
        ? defaultCostValue
        : undefined),
  );
  const [requestState, makeRequest] = useGraphQLRequest<UpdateReportResponse>();

  useEffect(() => {
    if (requestState.type !== 'REQUEST_SUCCESS') return;
    reloadAnswers();
    onNextSection();
  }, [requestState.type]);

  const handleSaveAnswer = useCallback(
    (answer: string) => {
      if (!client) return;
      const questionId = getFinalizeMarkerQuestionId(5, report.stage);
      if (!questionId) return;
      makeRequest(
        updateIndicatorAnswer(
          client,
          finalAnswers?.answerId,
          true,
          questionId,
          report.dbId,
          answer,
        ),
      );
    },
    [client, finalAnswers, report.stage],
  );

  const handleAutofill = useCallback(() => {
    setDirectCostState(initialAnswers.directCost);
    setOverheadState(initialAnswers.overheadCost);
    setInKindDonationState(initialAnswers.inKindDonations);
  }, [initialAnswers]);

  const handleNext = useCallback(() => {
    handleSaveAnswer(
      JSON.stringify({
        directCost: directCostState,
        overheadCost: overheadState,
        inKindDonations: inKindDonationState,
      }),
    );
  }, [
    directCostState,
    overheadState,
    inKindDonationState,
    reloadAnswers,
    onNextSection,
  ]);

  const initialTotalBudget = getTotalBudget(
    initialAnswers.directCost,
    initialAnswers.overheadCost,
    initialAnswers.inKindDonations,
  );
  const finalTotalBudget = getTotalBudget(
    directCostState,
    overheadState,
    inKindDonationState,
  );

  const isValid = validateFinalBudget(initialAnswers, {
    directCost: directCostState,
    overheadCost: overheadState,
    inKindDonations: inKindDonationState,
    answerId: finalAnswers?.answerId,
  });

  const forecastDate = report?.startDate
    ? moment(report?.startDate, 'YYYY-MM-DD').format('MMMM D, YYYY')
    : 'Forecast dates currently unavailable.';

  return (
    <StyledDiv>
      <TrueImpactText
        align="center"
        variant="heading-page"
        className="heading-page"
      >
        {isInterimStage
          ? 'Please update your program budget, if needed.'
          : 'Please update the final budget for your program.'}
      </TrueImpactText>
      <TrueImpactText
        variant="body-large"
        className="subheading-page"
        align="center"
      >
        This should represent your anticipated budget, not your budget to date.
      </TrueImpactText>
      <div className="table">
        <div className="header">
          <div className="stage-header">
            <TrueImpactCapsule className="capsule">
              Expense/Donation Type
            </TrueImpactCapsule>
          </div>
          <div className="forecast-header">
            <TrueImpactCapsule className="capsule">Forecast</TrueImpactCapsule>
            <TrueImpactText
              variant="body-small"
              color="grayDarkest"
              className="submitted-text"
            >
              {forecastDate}
            </TrueImpactText>
          </div>
          <div className="results-header">
            <TrueImpactCapsule className="capsule">
              {isInterimStage ? 'Updated Forecast' : 'Actual'}
            </TrueImpactCapsule>
            <TrueImpactText
              variant="body-small-bold"
              color="ctaPrimary"
              className="submitted-text"
              onClick={handleAutofill}
            >
              Auto-fill with forecasts
            </TrueImpactText>
          </div>
        </div>
        <div className="table-body">
          <div className="table-body-row table-body-row-border">
            <TrueImpactText variant="body-bold" className="text-label">
              Direct Cost
            </TrueImpactText>
            <TrueImpactText
              color="grayDark"
              className="text-cost text-cost-border"
            >
              {initialAnswers.directCost === defaultCostValue
                ? 'Not relevant to my program'
                : `$${initialAnswers.directCost}`}
            </TrueImpactText>
            <div className="dollar-field">
              {directCostState === defaultCostValue ? (
                <TrueImpactButton
                  variant="secondary-large"
                  onClick={() => setDirectCostState(undefined)}
                >
                  Add amount
                </TrueImpactButton>
              ) : (
                <TrueImpactDollarField
                  value={directCostState}
                  placeholder=""
                  onChange={setDirectCostState}
                />
              )}
            </div>
          </div>
          <div className="table-body-row table-body-row-border">
            <TrueImpactText variant="body-bold" className="text-label">
              Overhead Cost
            </TrueImpactText>
            <TrueImpactText
              color="grayDark"
              className="text-cost text-cost-border"
            >
              {initialAnswers.overheadCost === defaultCostValue
                ? 'Not relevant to my program'
                : `$${initialAnswers.overheadCost}`}
            </TrueImpactText>
            <div className="dollar-field">
              {overheadState === defaultCostValue ? (
                <TrueImpactButton
                  variant="secondary-large"
                  onClick={() => setOverheadState(undefined)}
                >
                  Add amount
                </TrueImpactButton>
              ) : (
                <TrueImpactDollarField
                  value={overheadState}
                  placeholder=""
                  onChange={setOverheadState}
                />
              )}
            </div>
          </div>
          <div className="table-body-row table-body-row-border">
            <TrueImpactText variant="body-bold" className="text-label">
              In-kind Donations
            </TrueImpactText>
            <TrueImpactText
              color="grayDark"
              className="text-cost text-cost-border"
            >
              {initialAnswers.inKindDonations === defaultCostValue
                ? 'Not relevant to my program'
                : `$${initialAnswers.inKindDonations}`}
            </TrueImpactText>
            <div className="dollar-field">
              {inKindDonationState === defaultCostValue ? (
                <TrueImpactButton
                  variant="secondary-large"
                  onClick={() => setInKindDonationState(undefined)}
                >
                  Add amount
                </TrueImpactButton>
              ) : (
                <TrueImpactDollarField
                  value={inKindDonationState}
                  placeholder=""
                  onChange={setInKindDonationState}
                />
              )}
            </div>
          </div>
          <div className="table-body-row dark-row">
            <TrueImpactText
              variant="body-bold"
              color="whiteBase"
              className="text-label"
            >
              Total Budget
            </TrueImpactText>
            <TrueImpactText color="whiteBase" className="text-cost dark-row">
              ${numberWithCommas(initialTotalBudget.toFixed(2))}
            </TrueImpactText>
            <TrueImpactText
              variant="body-bold"
              color="whiteBase"
              className="text-cost dark-row"
            >
              ${numberWithCommas(finalTotalBudget.toFixed(2))}
            </TrueImpactText>
          </div>
        </div>
      </div>
      <BottomBar
        disabled={false}
        hideBack={false}
        hideNext={!isValid}
        handleBack={onPreviousSection}
        handleNext={handleNext}
        backTitle="Back"
        nextTitle="Save & Continue"
      />
    </StyledDiv>
  );
};

export default FinalizeBudget;
